import { useTranslation } from 'next-i18next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { SignUpIndicator } from '../../../../components/Learning/learning-module/SignUpIndicator'
import ModulesList from '../../../../components/Learning/learning-modules/ModulesList'

import {
  calculatePrices,
  hasFreeAccess,
} from '../../../../components/Subscriptions/calculatePrices'
import { SubscribeButton } from '../../../../components/Subscriptions/cart/SubscribeButton'
import { SubscriptionType } from '../../../../components/Subscriptions/types'
import {
  useSeries,
  LearningSeriesWithModulesType,
} from '../../../../graphql/learning'
import {
  selectAuthenticatedUser,
  selectIsAuthenticating,
} from '../../../../modules/auth/selectors'
import { RootState } from '../../../../rootReducer'
import { AuthenticatedUser } from '../../../../types/userTypes'
import { getImageURL } from '../../../../url'
import { useSeriesCourseText } from '../../../../utils/series-course-text'
import { ContentView } from '@/components/Tracker/ContentView'

type Props = {
  resourceID: string
  resourceType: string
  resource: any
  series?: LearningSeriesWithModulesType
}

const Page: React.FunctionComponent<
  Props & {
    isAuthenticating: boolean
    currentUser: AuthenticatedUser
    isMember: boolean
  }
> = ({
  currentUser,
  resourceID,
  resourceType,
  resource,
  series: ssrSeries,
}) => {
  const { course, Course } = useSeriesCourseText()
  const router = useRouter()
  const { t } = useTranslation()

  const { data, loading: loadingModules } = ssrSeries
    ? { data: ssrSeries, loading: false }
    : useSeries(
        resourceType,
        resourceID,
        typeof router.query.seriesID === 'string'
          ? router.query.seriesID
          : router.query.seriesID[0],
        currentUser?.id
      )

  const series = data as LearningSeriesWithModulesType

  // Subscription vars
  const {
    payments_enabled: isSubscription,
    subscription_settings: subscriptionSettings,
    applicable_discount: discount,
    has_subscription: hasSubscription,
  } = !!series && !loadingModules && series

  const availableAsSeries = subscriptionSettings?.available_as_series

  const groupSubscriptionsEnabled =
    series?.library?.learning_subscriptions_enabled

  const freeAccess = hasFreeAccess(discount?.discount_type)

  // Price strings
  const { price: priceString, discountedPrice } =
    !!series &&
    !loadingModules &&
    !!isSubscription &&
    calculatePrices(series, subscriptionSettings?.series_price)

  const hasCoverImg = series && series?.image && series?.image?.path

  const showSubscribeCTA =
    isSubscription &&
    availableAsSeries &&
    !freeAccess &&
    !hasSubscription &&
    groupSubscriptionsEnabled &&
    data?.modules?.length > 1

  if (loadingModules) {
    return (
      <div
        className="page__container"
        style={{ paddingTop: 20, paddingBottom: 20 }}
      >
        {t('common:ui.activity_indicator')}
        ...
      </div>
    )
  }

  if (!data?.modules) {
    return (
      <div
        className="page__container"
        style={{ paddingTop: 20, paddingBottom: 20 }}
      >
        Not found
      </div>
    )
  }

  const results = data.modules.map(m => ({ ...m, series }))

  return !!series ? (
    <>
      <ContentView
        contentLabel="Learning Series"
        eventName="View"
        dwell
        eventProps={{
          label: series.id,
          'Content ID': series.id,
          'Content Title': series.title,
          source: resourceID,
          source_feed_url: global.document
            ? global.document.location.href
            : undefined,
        }}
      >
        {!currentUser && (
          <SignUpIndicator
            isSubscription={series?.payments_enabled}
            type={'series'}
            style={{
              top: '68px',
              position: 'sticky',
              zIndex: 9999,
              width: '100%',
              margin: '0px auto',
            }}
          />
        )}
        <div
          className="page__content resource resource--group fs-unmask"
          style={{
            paddingTop: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 'calc(100vh)',
          }}
        >
          <Head>
            <title>
              {series?.title} - {resource?.target.title}
            </title>
            <meta property="og:title" content={series.title} />
            {series.description && (
              <meta property="og:description" content={series.description} />
            )}
            {hasCoverImg && (
              <meta
                property="og:image"
                content={getImageURL('og-banner', 'cms', series.image.path)}
              />
            )}
          </Head>
          <div>
            <ModulesList
              returnToSeries
              results={results}
              isLoading={loadingModules}
              showBreadcrumbs={true}
              series={series}
              showDetails={true}
            />
          </div>
        </div>
        {showSubscribeCTA && (
          <div className="learning-list__series-subscribe-cta fs-unmask">
            <h2 className="learning-list__series-subscribe-title">
              <>
                {subscriptionSettings?.subscription_interval
                  ? 'Subscribe (Annually)'
                  : 'Purchase (Lifetime)'}{' '}
                to the complete {course} of{' '}
                {data.modules.length === 1
                  ? '1 module'
                  : `${data.modules.length} modules`}{' '}
                for
                <span> </span>
                <strong>
                  {discount ? (
                    <>
                      <span style={{ textDecoration: 'line-through' }}>
                        {priceString}
                      </span>{' '}
                      {discountedPrice}
                    </>
                  ) : (
                    priceString
                  )}
                </strong>
              </>
            </h2>
            <SubscribeButton
              productID={series?.id}
              type={SubscriptionType.SERIES}
              label={
                subscriptionSettings?.subscription_interval
                  ? 'Subscribe'
                  : 'Buy now'
              }
              libraryID={series?.library_id}
              block={false}
            />
          </div>
        )}
      </ContentView>
    </>
  ) : (
    <div
      className="page__container fs-unmask"
      style={{ paddingTop: 20, paddingBottom: 20 }}
    >
      <h1>{Course} not found.</h1>
    </div>
  )
}

export default connect((store: RootState, ownProps: Props) => {
  return {
    isAuthenticating: selectIsAuthenticating(store),
    currentUser: selectAuthenticatedUser(store),
    isMember:
      store.entities.entry_group &&
      store.entities.entry_group[ownProps.resourceID]?.connection_status ===
        'connected',
  }
})(Page)
